import { useQueryClient } from '@tanstack/react-query';
import { lazy, useState } from 'react';
import { useAppContext } from 'src/contexts/AppContext';
import { Content } from 'src/contexts/ContentContext';
import { Button } from 'src/core/ui';
import { useGetFulfillmentProviderSettings } from 'src/hooks/useGetFulfillmentProviderSettings';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { FulfillmentProviderCard } from 'src/navigations/Routes/Settings/Views/FulfillmentProviders/FulfillmentProviderCard';
import { StubhubFulfillmentLogo } from 'src/svgs/StubhubFulfillmentLogo';
import { TaciyonLogo } from 'src/svgs/TaciyonLogo';
import { TdcTransferLogo } from 'src/svgs/TdcTransferLogo';
import { TradedeskTransferLogo } from 'src/svgs/TradedeskTransferLogo';
import { ContentId } from 'src/utils/constants/contentId';
import { Feature } from 'src/WebApiController';

import * as styles from './FulfillmentProviders.css';
import { GroupedFulfillmentRule } from './FulfillmentRules/types';

/* These must be lazy-loaded to prevent "Cannot access 'FulfillmentProviders' before initialization" error.
 * During Storybook's static module bundling, Vite constructs the ESM module graph by evaluating
 * all imports synchronously. When it reaches Settings/Views/index.ts → FulfillmentProviders/index.ts
 * → FulfillmentProviders.tsx, the heavy synchronous imports (FulfillmentRules and its dependencies)
 * cause the module evaluation to complete after the export is referenced elsewhere in the graph.
 * Lazy loading breaks this cycle by deferring imports until render time.
 **/

const FulfillmentRules = lazy(() =>
  import('./FulfillmentRules').then((module) => ({
    default: module.FulfillmentRules,
  }))
);

const FulfillmentRuleOverrideModal = lazy(() =>
  import('./FulfillmentRules/FulfillmentRuleOverrideModal').then((module) => ({
    default: module.FulfillmentRuleOverrideModal,
  }))
);

export const MapFulfillmentProviderToIcon = (providerName: string) => {
  // TODO: switch to mapping icon to guid instead of name once they are created in the DB
  switch (providerName.toLowerCase()) {
    case 'stubhub fulfillment':
      return <StubhubFulfillmentLogo />;
    case 'taciyon':
      return <TaciyonLogo />;
    case 'tradedesk transfer':
      return <TradedeskTransferLogo />;
    case 'transfer':
      return <TdcTransferLogo />;
    default:
      return null;
  }
};

export const FulfillmentProviders = () => {
  const { fulfillmentProviderSettings } = useGetFulfillmentProviderSettings();

  const { activeAccountWebClientConfig } = useAppContext();
  const hasFulfillmentRulesVenueLevelFeature = useUserHasFeature(
    Feature.FulfillmentRulesVenueLevel
  );
  const queryClient = useQueryClient();
  const [isOverrideModalOpen, setIsOverrideModalOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<
    GroupedFulfillmentRule | undefined
  >();

  const providers = fulfillmentProviderSettings ?? [];

  const refreshRules = async () => {
    // Force an immediate refetch instead of just invalidating
    await queryClient.refetchQueries({
      queryKey: [
        'VenueLevelFulfillmentRules',
        activeAccountWebClientConfig.activeAccountId,
      ],
    });
  };

  const handleAddOverride = () => {
    setEditingGroup(undefined);
    setIsOverrideModalOpen(true);
  };

  const handleEditGroup = (group: GroupedFulfillmentRule) => {
    setEditingGroup(group);
    setIsOverrideModalOpen(true);
  };

  const handleCloseOverrideModal = () => {
    setIsOverrideModalOpen(false);
    setEditingGroup(undefined);
  };

  return (
    <div>
      <div className={styles.root}>
        <div className={styles.center}>
          <h1 className={styles.pageName}>
            <Content id={ContentId.FulfillmentProviders} />
          </h1>
          <div className={styles.subtleText}>
            <Content id={ContentId.FulfillmentProvidersSubText} />
          </div>
          <div className={styles.rootContainer}>
            <div className={styles.cardGrid}>
              {providers.length > 0 ? (
                providers.map((provider) => (
                  <FulfillmentProviderCard
                    key={provider.fulfillmentProviderSellerUserId}
                    logo={MapFulfillmentProviderToIcon(
                      provider.fulfillmentProviderName
                    )}
                    fulfillmentProviderSellerUserId={
                      provider.fulfillmentProviderSellerUserId
                    }
                    name={provider.fulfillmentProviderName}
                    description={provider.descriptionMessage}
                    enabled={provider.isEnabled}
                  />
                ))
              ) : (
                <div>
                  <Content id={ContentId.NoFulfillmentProviders} />
                </div>
              )}
            </div>
          </div>

          {hasFulfillmentRulesVenueLevelFeature && (
            <>
              <div className={styles.sectionDivider} />

              <div className={styles.sectionHeader}>
                <h2 className={styles.pageName}>Fulfillment Rules</h2>
                <Button variant="outline" onClick={handleAddOverride}>
                  Add rule
                </Button>
              </div>
              <FulfillmentRules onEditRuleGroup={handleEditGroup} />
            </>
          )}
        </div>
      </div>

      {isOverrideModalOpen && (
        <FulfillmentRuleOverrideModal
          isOpen={true}
          onClose={handleCloseOverrideModal}
          onCreateSuccess={refreshRules}
          initialProviderId={editingGroup?.fulfillerUserId}
          initialVenues={
            editingGroup
              ? editingGroup.venueIds.map((id, i) => ({
                  id: String(id),
                  name: editingGroup.venueNames[i],
                  ruleId: editingGroup.ruleIds[i],
                }))
              : undefined
          }
        />
      )}
    </div>
  );
};
